import React from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "~/components/ui/button";
import { CoachProfileImage } from "~/components/ui/coach-profile-image";
import { Quote } from "lucide-react";
import { GradientBackground } from "~/components/ui/gradient-background";
import { Feature, hasFeature } from "~/lib/has-feature";
import { CoachDetails } from "~/components/coach-details";
import { SocialLink } from "~/components/ui/social-link";
import { PlanBadge } from "~/components/plan-badge";
import { MembershipPlan } from "~/lib/schemas";
import { CDNImage } from "~/components/ui/cdn-image";
import { PreGeneratedProfileBanner } from "~/components/ui/pre-generated-profile-banner";
import { Tab, TabList } from "~/components/ui/tabs";
import {
  shouldShowCoachCTAs,
  shouldShowBookingCTAs,
  shouldShowMessagingCTAs,
  getCoachCTAText,
} from "~/lib/coach-cta-utils";
import type { CoachPublicData } from "~/shared/loaders";

interface CoachProfileLayoutProps {
  coach: CoachPublicData;
  resourceCount?: number;
  routePath?: string;
  children: React.ReactNode;
}

export function CoachProfileLayout({
  coach,
  resourceCount = 0,
  routePath = "",
  children,
}: CoachProfileLayoutProps) {
  if (!coach?.profile) {
    return <div>Loading...</div>;
  }

  const hasTabs =
    hasFeature(coach, Feature.Resources) ||
    hasFeature(coach, Feature.OfferPage);

  return (
    <>
      <div className="mx-auto my-4 flex max-w-5xl flex-col px-4 md:mt-8">
        {coach.profile.isPreGeneratedProfile && (
          <PreGeneratedProfileBanner
            coachName={coach.firstName}
            fullName={coach.fullName}
            username={coach.username}
          />
        )}
        {hasFeature(coach, Feature.FancyProfile) &&
          coach.profile.heroImageSrc && (
            <CDNImage
              src={coach.profile.heroImageSrc}
              width={1024}
              height={240}
              className="mb-8 h-60 w-full overflow-hidden rounded-2xl object-cover md:mb-12"
              alt=""
            />
          )}

        <div className="mb-4 flex w-full flex-col gap-8 sm:flex-row">
          <div className="basis-0 grow-1">
            <CoachProfileImage
              coach={coach}
              profilePictureUrl={coach.profilePictureUrl}
              fullName={coach.fullName}
            />

            {hasFeature(coach, Feature.FancyProfile) &&
              coach.profile.brandLogoUrl && (
                <div className="mt-4 flex items-center gap-2 px-4">
                  <CDNImage
                    src={coach.profile.brandLogoUrl}
                    width={48}
                    height={48}
                    alt={`${coach.profile.brandName || coach.fullName}'s brand logo`}
                    className="h-12 w-12 rounded-full border object-cover"
                  />
                  {coach.profile.brandName && (
                    <div className="text-text-primary font-medium">
                      {coach.profile.brandName}
                    </div>
                  )}
                </div>
              )}
          </div>

          <div className="basis-0 grow-3 min-w-0 flex flex-col items-stretch justify-center gap-8">
            <div className="flex flex-col items-center justify-between gap-4 sm:items-start md:flex-row">
              <div className="flex flex-col items-center gap-0 text-center sm:items-start sm:text-left">
                <Link
                  to="/$username"
                  params={{ username: coach.username }}
                  className="ring-brand-100 flex items-center gap-2 rounded-lg ring-offset-2 transition hover:ring"
                >
                  <h1 className="font-heading text-text-primary text-4xl">
                    {coach.fullName}
                  </h1>
                  {coach.membershipPlan !== MembershipPlan.Standard && (
                    <PlanBadge membershipPlan={coach.membershipPlan} />
                  )}
                </Link>
                <div className="text-text-secondary mb-4 flex items-center gap-2 text-xl">
                  {coach.profile.coachingTitle}
                </div>
                {coach.profile.openingStatement && (
                  <div className="text-text-tertiary text-sm">
                    <Quote className="mr-2 inline-block size-4 rotate-180 text-gray-200" />
                    {coach.profile.openingStatement}
                    <Quote className="ml-2 inline-block rotate-0 size-4 text-gray-200" />
                  </div>
                )}
              </div>

              {shouldShowCoachCTAs(coach) && (
                <div className="flex shrink-0 flex-row items-center justify-end gap-2">
                  {shouldShowMessagingCTAs(coach) ? (
                    <Link
                      to="/$username/contact"
                      params={{ username: coach.username }}
                    >
                      <Button
                        variant={
                          hasFeature(coach, Feature.FancyProfile)
                            ? "fancy"
                            : "default"
                        }
                      >
                        {getCoachCTAText(coach)}
                      </Button>
                    </Link>
                  ) : shouldShowBookingCTAs(coach) ? (
                    <Link
                      to="/$username/book"
                      params={{ username: coach.username }}
                    >
                      <Button
                        variant={
                          hasFeature(coach, Feature.FancyProfile)
                            ? "fancy"
                            : "default"
                        }
                      >
                        {getCoachCTAText(coach)}
                      </Button>
                    </Link>
                  ) : null}
                </div>
              )}
            </div>

            {hasTabs && (
              <TabList>
                <Tab staticLink href={`${routePath}/${coach.username}`}>
                  About
                </Tab>
                {hasFeature(coach, Feature.Resources) && 0 < resourceCount && (
                  <Tab
                    staticLink
                    href={`${routePath}/${coach.username}/resources`}
                  >
                    Resources
                  </Tab>
                )}
                {hasFeature(coach, Feature.OfferPage) && (
                  <Tab staticLink href={`${routePath}/${coach.username}/offer`}>
                    Offer
                  </Tab>
                )}
              </TabList>
            )}
          </div>
        </div>

        <div className="flex w-full flex-col-reverse gap-8 md:flex-row">
          <div className="w-full basis-0 grow-1">
            <CoachDetails profile={coach.profile} />

            <div className="flex items-center justify-center gap-2 pt-4">
              <SocialLink
                href={`https://dreamcoachmatch.com/${coach.username}`}
                icon="dcm"
                ariaLabel="Dream Coach Match Profile"
              />
              {coach.profile.website && (
                <SocialLink
                  href={coach.profile.website}
                  icon="globe"
                  ariaLabel="Website"
                />
              )}
              {coach.profile.facebookProfile && (
                <SocialLink
                  href={coach.profile.facebookProfile}
                  prefix="https://facebook.com/"
                  icon="facebook"
                  ariaLabel="Facebook"
                />
              )}
              {coach.profile.instagramProfile && (
                <SocialLink
                  href={coach.profile.instagramProfile}
                  prefix="https://instagram.com/"
                  icon="instagram"
                  ariaLabel="Instagram"
                />
              )}
              {coach.profile.linkedinProfile && (
                <SocialLink
                  href={coach.profile.linkedinProfile}
                  prefix="https://linkedin.com/in/"
                  icon="linkedin"
                  ariaLabel="LinkedIn"
                />
              )}
              {coach.profile.youtubeChannel && (
                <SocialLink
                  href={coach.profile.youtubeChannel}
                  prefix="https://www.youtube.com/"
                  icon="youtube"
                  ariaLabel="YouTube"
                />
              )}
            </div>
          </div>

          <div className="grow-3 basis-0 min-w-0 flex flex-col items-stretch gap-8">
            {children}
          </div>
        </div>
      </div>
      {hasFeature(coach, Feature.FancyProfile) && <GradientBackground />}
    </>
  );
}
